import { useEffect, useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { MEDIA } from '@/data/site';
import { navigate } from '@/hooks/useRoute';
import { verifyHypPayment } from '@/services/paymentService';
import { trackMielleyEvent } from '@/lib/mielleyAnalytics';
import type { VerifyPaymentResult } from '@/types/payment';


type ResultKind = 'success' | 'error' | 'cancel';
type ViewState = 'verifying' | 'paid' | 'failed' | 'uncertain';

const pendingOrderNumber = () => {
  try { return JSON.parse(sessionStorage.getItem('mielley_pending_order') || '{}')?.orderNumber || ''; }
  catch { return ''; }
};

export function PaymentResultPage({ kind }: { kind: ResultKind }) {
  const { clearCart } = useStore();
  const [view, setView] = useState<ViewState>('verifying');
  const [result, setResult] = useState<VerifyPaymentResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const verify = async () => {
      if (!window.location.search) {
        if (!active) return;
        setView(kind === 'success' ? 'uncertain' : 'failed');
        return;
      }
      try {
        const data = await verifyHypPayment(window.location.search);
        if (!active) return;
        setResult(data);
       if (data.paid && data.valid) {
  try {
    const pending = JSON.parse(
      sessionStorage.getItem('mielley_pending_order') || '{}'
    );

    const transactionId = String(
      data.transactionId ||
      data.orderNumber ||
      pending.orderNumber ||
      ''
    ).trim();

    const value = Number(pending.value || 0);
    const currency = String(pending.currency || 'ILS');

    const items = Array.isArray(pending.items)
      ? pending.items
      : [];

    if (
      transactionId &&
      value > 0 &&
      items.length > 0
    ) {
      const purchaseKey =
        `mielley_ga4_purchase_${transactionId}`;

      if (
        sessionStorage.getItem(purchaseKey) !== '1'
      ) {
        // חשוב: מסמנים לפני השליחה כדי שרענון
        // של דף ההצלחה לא ייצור רכישה כפולה.
        sessionStorage.setItem(purchaseKey, '1');

        void trackMielleyEvent({
          eventName: 'purchase',
          path:
            window.location.pathname +
            window.location.search,

          metadata: {
            transaction_id: transactionId,
            value,
            currency,
            items,
          },
        });
      }
    }
  } catch {}

  clearCart();
  sessionStorage.removeItem('mielley_pending_order');
  setView('paid');
} else {
          setView(data.status === 'payment_failed' || kind !== 'success' ? 'failed' : 'uncertain');
        }
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'לא הצלחנו לאמת את העסקה כרגע.');
        setView('uncertain');
      }
    };
    void verify();
    return () => { active = false; };
  }, [kind]);

  const orderNumber = result?.orderNumber || pendingOrderNumber();

  return <div className="payment-result-page" dir="rtl">
    <header className="checkout-header"><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}><img src={MEDIA.logo} alt="MIELLEY" /></a><span>תשלום מאובטח</span></header>
    <main className="payment-result-wrap">
      {view === 'verifying' && <section className="payment-result-card verifying"><div className="payment-spinner" /><h1>מאמתים את התשלום</h1><p>עוד רגע. אנחנו מוודאים מול HYP שהעסקה אושרה לפני שנאשר את ההזמנה.</p></section>}

      {view === 'paid' && <section className="payment-result-card success-state"><b className="result-icon">✓</b><span className="result-kicker">PAYMENT CONFIRMED</span><h1>תודה, ההזמנה התקבלה</h1><p>התשלום אומת בהצלחה וההזמנה נשמרה במערכת.</p>{orderNumber && <div className="result-order"><span>מספר הזמנה</span><strong>{orderNumber}</strong></div>}{result?.transactionId && <small>מזהה עסקה: {result.transactionId}</small>}<div className="result-actions"><button className="btn gold" onClick={() => navigate('/catalog')}>להמשך קניות</button><button className="btn" onClick={() => navigate('/account')}>האזור האישי</button></div></section>}

      {view === 'failed' && <section className="payment-result-card failed-state"><b className="result-icon">×</b><span className="result-kicker">PAYMENT NOT COMPLETED</span><h1>התשלום לא הושלם</h1><p>{result?.message || 'לא בוצע חיוב מאושר. הסל נשמר ואפשר לנסות שוב.'}</p>{orderNumber && <div className="result-order"><span>מספר ניסיון</span><strong>{orderNumber}</strong></div>}<div className="result-actions"><button className="btn gold" onClick={() => navigate('/checkout')}>לנסות שוב</button><button className="btn" onClick={() => navigate('/cart')}>חזרה לסל</button></div></section>}

      {view === 'uncertain' && <section className="payment-result-card uncertain-state"><b className="result-icon">!</b><span className="result-kicker">VERIFICATION REQUIRED</span><h1>עדיין לא אישרנו את ההזמנה</h1><p>{error || result?.message || 'לא הצלחנו לקבל כרגע אימות חד-משמעי מ-HYP. הסל לא נמחק וההזמנה לא סומנה כשולמה.'}</p>{orderNumber && <div className="result-order"><span>מספר ניסיון</span><strong>{orderNumber}</strong></div>}<div className="result-actions"><button className="btn gold" onClick={() => window.location.reload()}>בדיקה מחדש</button><button className="btn" onClick={() => navigate('/cart')}>חזרה לסל</button></div></section>}
    </main>
  </div>;
}
